<script setup lang="ts">
import { DateTime } from 'luxon';
import { useFormValues } from 'vee-validate';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import JobConfigurationFormAssignment from '../../jobConfigurations/components/JobConfigurationFormAssignment.vue';
import JobExecutionReviewDisplay from './JobExecutionReviewDisplay.vue';
import EntityLink from '@/components/EntityLink/EntityLink.vue';
import { JobOccurrenceDto, ProtocolDto } from '@/api/generator';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { I18nKey } from '@/utils/typings';
import Due from './Due.vue';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import FormSelectionDisplay from '@/components/forms/display/FormSelectionDisplay.vue';
import { computed } from 'vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { SelectItemTypeObject } from '@/components/forms/types';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';

const values = useFormValues<JobOccurrenceDto>();

const protocols = computed<SelectItemTypeObject<ProtocolDto>[] | undefined>(() =>
  values.value.protocols?.map((protocol) => {
    return {
      title: protocol.name,
      iuid: protocol.iuid,
      value: protocol,
    };
  }),
);
</script>

<template>
  <FormSection>
    <v-row justifySm="start">
      <v-col cols="10">
        <v-row>
          <v-col>
            <FormLabel :label="$t('JobConfiguration_one')" />
            <EntityLink
              :id="values.jobConfigurationId ?? undefined"
              :name="values.jobConfigurationName || ''"
              :iuid="values.jobConfigurationIuid"
              :link="!!values.jobConfigurationId"
              openInTab
              type="jobConfiguration"
            />
          </v-col>

          <v-col>
            <FormLabel name="start" :label="$t('StartTime')" class="gutterTop">
              <DateTimeFormatter :value="values.start" />
            </FormLabel>
          </v-col>

          <v-col>
            <FormLabel name="end" :label="$t('EndTime')" class="gutterTop">
              <DateTimeFormatter :value="values.end" />
              <div>
                <Due
                  :state="values.dueState"
                  :startDate="values.start ? DateTime.fromISO(values.start) : null"
                  :endDate="values.end ? DateTime.fromISO(values.end) : null"
                />
              </div>
            </FormLabel>
          </v-col>

          <v-col>
            <FormLabel name="frequency" :label="$t('Recurrence')" class="gutterTop">
              {{ $t(`FrequencyType.${values.frequency}` as I18nKey) }}
            </FormLabel>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <VeeTextarea
              :label="$t('Description')"
              name="jobConfigurationDescription"
              class="gutterTop"
            />
            <VeeAttachments class="mt-1" name="attachments" mode="details" />
          </v-col>
          <v-col cols="6">
            <FormLabel :label="$t('Protocol_other')" />
            <FormSelectionDisplay :value="protocols">
              <template #itemTitle="{ iuid, title }">
                <IdentifierLabel :label="title" :identifier="iuid" />
              </template>
            </FormSelectionDisplay>
          </v-col>
        </v-row>

        <v-divider class="mt-4 mb-4" />

        <v-row>
          <v-col cols="6">
            <div class="text-h6 mb-2">
              {{ $t('JobConfigurationExecutionAssignmentHeader') }}
            </div>
            <JobConfigurationFormAssignment />
          </v-col>

          <v-col cols="6">
            <div class="text-h6 mb-2">
              {{ $t('JobConfigurationReviewAssignmentHeader') }}
            </div>
            <JobExecutionReviewDisplay />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="2">
        <EntityChangeBlock class="gutterTop" :entity="values" />
      </v-col>
    </v-row>
  </FormSection>
</template>
